import React from "react";
import { spring, useCurrentFrame, useVideoConfig } from "remotion";

interface OutroProps {
  keywords?: string[];
}

export const GridMotionOutro: React.FC<OutroProps> = ({ keywords = [] }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  void keywords;

  const entrance = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: { damping: 13, stiffness: 100 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        opacity: entrance,
        transform: `scale(${0.85 + entrance * 0.15})`,
        padding: "0 60px",
        boxSizing: "border-box",
      }}
    >
      {/* HUD Scanner Outro Card */}
      <div
        style={{
          border: "2px solid rgba(0, 242, 254, 0.4)",
          padding: "50px 40px",
          textAlign: "center",
          background: "rgba(4, 5, 15, 0.85)",
          backdropFilter: "blur(20px)",
          borderRadius: "16px", // Sharp HUD corners
          boxShadow:
            "0 25px 50px rgba(0, 0, 0, 0.6), 0 0 35px rgba(0, 242, 254, 0.2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "30px",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Neon HUD corners */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "12px",
            height: "12px",
            borderLeft: "3px solid #ff007f",
            borderTop: "3px solid #ff007f",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "12px",
            height: "12px",
            borderRight: "3px solid #ff007f",
            borderTop: "3px solid #ff007f",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "12px",
            height: "12px",
            borderLeft: "3px solid #bef264",
            borderBottom: "3px solid #bef264",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "12px",
            height: "12px",
            borderRight: "3px solid #bef264",
            borderBottom: "3px solid #bef264",
          }}
        />

        {/* Ambient magenta/cyan radial gradient lights */}
        <div
          style={{
            position: "absolute",
            top: "-150px",
            width: "350px",
            height: "350px",
            background:
              "radial-gradient(circle, rgba(255, 0, 127, 0.2) 0%, transparent 70%)",
            filter: "blur(40px)",
            zIndex: -1,
            pointerEvents: "none",
          }}
        />

        {/* Dynamic Rotating Scanner Rings for Instagram Icon */}
        <div
          style={{
            position: "relative",
            width: "120px",
            height: "120px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          {/* Outer rotating HUD ring */}
          <div
            style={{
              position: "absolute",
              width: "120px",
              height: "120px",
              border: "1.5px dashed rgba(190, 242, 100, 0.5)", // Lime
              borderRadius: "50%",
              transform: `rotate(${frame * 2}deg)`,
              boxShadow: "0 0 10px rgba(190, 242, 100, 0.15)",
            }}
          />
          {/* Inner rotating HUD ring */}
          <div
            style={{
              position: "absolute",
              width: "100px",
              height: "100px",
              border: "1px dashed rgba(255, 0, 127, 0.4)",
              borderRadius: "50%",
              transform: `rotate(${-frame * 3}deg)`,
            }}
          />

          {/* Core Instagram Icon */}
          <div
            style={{
              zIndex: 5,
              background: "rgba(255, 0, 127, 0.1)",
              border: "1.5px solid rgba(255, 0, 127, 0.4)",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0 0 15px rgba(255, 0, 127, 0.2)",
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ff007f"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
          </div>
        </div>

        {/* CTA Typography */}
        <div>
          <h2
            style={{
              fontFamily: "var(--font-title)",
              fontSize: "50px",
              fontWeight: 900,
              background: "linear-gradient(135deg, #ffffff 40%, #bef264 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              margin: "0 0 12px 0",
              textTransform: "uppercase",
              letterSpacing: "-1px",
            }}
          >
            Stream Active
          </h2>

          <p
            style={{
              fontFamily: "var(--font-title)",
              fontSize: "22px",
              fontWeight: 800,
              color: "#ffffff",
              margin: 0,
              opacity: 0.7,
              lineHeight: 1.4,
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Tap Follow to stay connected
          </p>
        </div>

        {/* Glowing Tech handle tag */}
        <div
          style={{
            background: "rgba(4, 5, 15, 0.6)",
            border: "1.5px solid #00f2fe",
            padding: "14px 36px",
            borderRadius: "4px", // Sharp HUD corners
            boxShadow: "0 0 20px rgba(0, 242, 254, 0.25)",
            marginTop: "10px",
            position: "relative",
          }}
        >
          {/* Active border nodes */}
          <span
            style={{
              position: "absolute",
              left: -2,
              top: "50%",
              width: "4px",
              height: "4px",
              background: "#bef264",
              transform: "translateY(-50%)",
            }}
          />
          <span
            style={{
              position: "absolute",
              right: -2,
              top: "50%",
              width: "4px",
              height: "4px",
              background: "#bef264",
              transform: "translateY(-50%)",
            }}
          />

          <span
            style={{
              fontFamily: "var(--font-title)",
              fontSize: "20px",
              fontWeight: 900,
              color: "#00f2fe",
              textTransform: "uppercase",
              letterSpacing: "3px",
            }}
          >
            @TheDigitalBullet
          </span>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
